import { useDisclosure } from '@chakra-ui/react'
import { UseQueryResult } from '@tanstack/react-query'
import cn from 'clsx'
import { Dispatch, FC, SetStateAction } from 'react'

import DialogWindow from '@/components/DialogWindow'
import { Button, Typography } from '@/components/ui'
import CustomSelectLanguage from '@/components/ui/select/CustomSelectLanguage'

import FileQuestionMark from '@/assets/icons/file-question-mark.svg'

import { IShotlist } from '@/types/shotlist.interface'
import { ILanguage } from '@/types/task.interface'

import { transcribeShotlistOriginal } from '../actions/transcribeShotlistOriginal'

type Disclosure = ReturnType<typeof useDisclosure>

interface IUploadOriginalTextProps {
	taskId?: string
	languages: ILanguage[]
	onUploadSrt: () => void
	selectedTranscribingLang: ILanguage | null
	setSelectedTranscribingLang: Dispatch<SetStateAction<ILanguage | null>>
	selectedSrtLang: ILanguage | null
	setSelectedSrtLang: Dispatch<SetStateAction<ILanguage | null>>
	setSelectedSrtFile: Dispatch<SetStateAction<File | null>>
	selectLanguageDialog: Disclosure
	selectSrtDialog: Disclosure
	isTranslation: boolean
	watchTask: (
		taskId: string,
		onUpdate?: (shotlist: IShotlist) => void,
		onFinished?: (shotlist: IShotlist) => void
	) => Promise<void>
	clearWatch: () => void
	setIsTranscribing: Dispatch<SetStateAction<boolean>>
	refetchShotlist: UseQueryResult<IShotlist, unknown>['refetch']
}

const UploadOriginalText: FC<IUploadOriginalTextProps> = ({
	languages,
	onUploadSrt,
	selectedTranscribingLang,
	setSelectedTranscribingLang,
	selectedSrtLang,
	setSelectedSrtLang,
	setSelectedSrtFile,
	selectLanguageDialog,
	selectSrtDialog,
	isTranslation,
	taskId,
	watchTask,
	clearWatch,
	setIsTranscribing,
	refetchShotlist
}) => {
	const onTranscribe = async (language: string) => {
		if (!language || !selectedTranscribingLang) return

		selectLanguageDialog.onClose()

		await transcribeShotlistOriginal({
			taskId,
			language,
			selectedTranscribingLang,
			watchTask,
			clearWatch,
			setIsTranscribing,
			setSelectedTranscribingLang,
			refetchShotlist
		})
	}

	return (
		<>
			{/* Select language for transcribation modal window */}
			<DialogWindow
				handleClick={() => {
					if (!selectedTranscribingLang) return
					onTranscribe(selectedTranscribingLang.code)
				}}
				open={selectLanguageDialog.open}
				onClose={() => {
					setSelectedTranscribingLang(null)
					selectLanguageDialog.onClose()
				}}
				titleText='Select source language'
				body={
					<CustomSelectLanguage
						value={selectedTranscribingLang}
						onChange={setSelectedTranscribingLang}
						options={languages || []}
						placeholder='Select language'
					/>
				}
				noButtonText='Cancel'
				yesButtonText='Create'
			/>

			{/* Select language for uploading SRT modal window */}
			<DialogWindow
				handleClick={onUploadSrt}
				open={selectSrtDialog.open}
				onClose={() => {
					setSelectedSrtLang(null)
					setSelectedSrtFile(null)
					selectSrtDialog.onClose()
				}}
				titleText='Select SRT language'
				body={
					<CustomSelectLanguage
						value={selectedSrtLang}
						onChange={setSelectedSrtLang}
						options={languages || []}
						placeholder='Select language'
					/>
				}
				noButtonText='Cancel'
				yesButtonText='Create'
			/>
			<div className={cn('!mx-auto mt-[15%]', isTranslation && '!mt-[35%]')}>
				<FileQuestionMark className='mx-auto mb-8' />
				<Typography
					variant='text-large'
					className='text-center !font-[600] mb-7'
				>
					There are no SRT file yet
				</Typography>
				<div className='flex flex-col items-center mx-auto gap-3'>
					<Button
						onClick={() => selectLanguageDialog.onOpen()}
						className='px-[145.5px] py-[14px] rounded-[8px] bg-gray-100 hover:bg-gray-200'
						classNameText='text-gray-600'
					>
						Create
					</Button>
					<Button className='py-[14px] px-0'>
						<label
							htmlFor='srt-upload'
							className='px-[145.5px] py-[15.5px] rounded-[8px] bg-gray-600 text-white hover:bg-black z-0 cursor-pointer'
						>
							Upload
						</label>
						<input
							id='srt-upload'
							type='file'
							name='file'
							accept='.srt,.vtt,.ass,.ssa,.sbv,.sub,.lrc,.ttml,.dfxp,.scc,text/vtt,application/x-subrip,text/plain'
							className='hidden'
							multiple={false}
							onChange={e => {
								const file = e.target.files?.[0]
								if (!file) return

								setSelectedSrtFile(file)
								setSelectedSrtLang(null)
								selectSrtDialog.onOpen()
								e.target.value = ''
							}}
						/>
					</Button>
				</div>
			</div>
		</>
	)
}

export default UploadOriginalText
